import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import LayoutSelector2025 from '../../components/LayoutSelector2025';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection'; // ✅ Text-Refactoring 2025 System
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import AnimationSettingsSection from '../WidgetEditComponents/AnimationSettingsSection';
import ButtonWidgetStyle from '../WidgetEditComponents/ButtonWidgetStyle';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function VideoTeaserEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Widget-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.layoutType);
    
    // Image Upload States
    const [isUploadingHeader, setIsUploadingHeader] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);
    const [isUploadingIcon, setIsUploadingIcon] = useState(false);
    const [isUploadingDecoration, setIsUploadingDecoration] = useState(false);

    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 VideoTeaserEdit initialized - layout variations now active');
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.layoutType;
        
        // ✅ CRITICAL: Only apply layout variations on ACTUAL layout changes, not widget switches
        if (!isInitialized) {
            console.log('🚫 VideoTeaserEdit: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 VideoTeaserEdit: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 VideoTeaserEdit REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS - VEREINFACHT
        const layoutVariations = {
            'classic-video-right': {
                height: 'auto',
                heightMobile: 'auto',
                videoContainerWidth: 45,
                videoContainerWidthMobile: 80,
                videoBorderRadius: 12,
                videoBorderRadiusMobile: 8,
                textAlignment: 'left',
                textAlignmentMobile: 'center',
                contentSpacing: 20,
                contentSpacingMobile: 15,
                showVideo: true,
                showDecorationImage: false
            },
            'video-left': {
                height: 'auto',
                heightMobile: 'auto',
                videoContainerWidth: 45,
                videoContainerWidthMobile: 80,
                videoBorderRadius: 12,
                videoBorderRadiusMobile: 8,
                textAlignment: 'left',
                textAlignmentMobile: 'center',
                contentSpacing: 20,
                contentSpacingMobile: 15,
                showVideo: true,
                showDecorationImage: false
            },
            'video-dominant': {
                height: 'auto',
                heightMobile: 'auto',
                videoContainerWidth: 65,
                videoContainerWidthMobile: 90,
                videoBorderRadius: 12,
                videoBorderRadiusMobile: 8,
                textAlignment: 'left',
                textAlignmentMobile: 'center',
                contentSpacing: 20,
                contentSpacingMobile: 15,
                showVideo: true,
                showDecorationImage: false
            },
            'video-top-centered': {
                height: 'auto',
                heightMobile: 'auto',
                videoContainerWidth: 80,
                videoContainerWidthMobile: 90,
                videoBorderRadius: 12,
                videoBorderRadiusMobile: 8,
                textAlignment: 'center',
                textAlignmentMobile: 'center',
                contentSpacing: 50,
                contentSpacingMobile: 30,
                videoGap: 40,
                videoGapMobile: 30,
                showVideo: true,
                showDecorationImage: true,
                decorationImage: '/img/gold_glitter.png',
                decorationImageSize: 120,
                decorationImageSizeMobile: 100,
                decorationImagePositionX: 85,
                decorationImagePositionY: 15,
                decorationImageOpacity: 25
            },
            'mixed-centered': {
                height: 'auto',
                heightMobile: 'auto',
                textAlignment: 'center',
                textAlignmentMobile: 'center',
                contentSpacing: 30,
                contentSpacingMobile: 20,
                videoGap: 30,
                videoGapMobile: 25,
                showVideo: true,
                showHeaderImage: false,
                showDecorationImage: false
            }
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ KRITISCH: BATCH UPDATE - Alle Werte auf einmal setzen
            const safePosition = widget.position !== undefined && widget.position !== null ? widget.position : 0;
            const updatedEditData = { ...editData, ...variations };
            
            // ✅ FIREBASE-SAFE: Position immer mitgesendet
            onUpdate({ ...updatedEditData, position: safePosition });
            
            console.log(`✅ VideoTeaserEdit layout variations applied via batch update`);
        }
    }, [editData.layoutType, isInitialized, previousLayoutType]);

    // 🔍 DEBUG: Zeige aktuelle Mobile Properties
    console.log('🔍 VideoTeaserEdit Mobile Properties Check:', {
        layoutType: editData.layoutType,
        // ✅ VIDEO PROPERTIES
        videoContainerWidth: editData.videoContainerWidth,
        videoContainerWidthMobile: editData.videoContainerWidthMobile,
        videoBorderRadius: editData.videoBorderRadius,
        videoBorderRadiusMobile: editData.videoBorderRadiusMobile,
        // ✅ SPACING PROPERTIES
        contentSpacing: editData.contentSpacing,
        contentSpacingMobile: editData.contentSpacingMobile,
        // ✅ IMAGE PROPERTIES
        iconHeight: editData.iconHeight,
        iconHeightMobile: editData.iconHeightMobile,
        decorationImageSize: editData.decorationImageSize,
        decorationImageSizeMobile: editData.decorationImageSizeMobile
    });

    // ✅ LAYOUT CHANGE HANDLER
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log(`🎨 VideoTeaserEdit layout change: ${layoutId}`, layoutConfig);
        const fakeEvent = {
            target: { name: 'layoutType', value: layoutId, type: 'text' }
        };
        handleInputChange(fakeEvent);
    };

    // ✅ TEXT-REFACTORING 2025: Korrekte textConfig mit Show/Hide Support
    const textConfig = [
        {
            fieldName: 'richText',
            label: 'Rich Text',
            type: 'richtext', // ✅ Rich Text Editor mit Bold-Funktionalität
            placeholder: 'Enter your rich text with <strong>bold</strong> formatting...',
            height: '150px',
            fontField: 'richTextFont',
            fontType: 'big',
            effectField: 'richTextEffect',
            showField: 'showRichText' // ✅ Show/Hide Toggle Support
        },
        // ✅ NEU: Highlight Words Feld hinzufügen
        {
            fieldName: 'richTextHighlightWords',
            label: 'Rich Text - Highlight Words',
            type: 'input',
            placeholder: 'energy, power (comma separated)',
            description: 'Enter words to highlight with different styling',
            fontField: 'richTextHighlightFont',
            fontType: 'small', // ✅ Anderer Font-Pool als Rich Text
            effectField: 'richTextHighlightEffect'
        },
        {
            fieldName: 'descriptionText',
            label: 'Description',
            type: 'richtext', // ✅ Rich Text Editor mit Bold-Funktionalität
            placeholder: 'Enter description with <strong>bold</strong> formatting...',
            height: '120px',
            fontField: 'descriptionFont',
            fontType: 'small',
            effectField: 'descriptionEffect',
            showField: 'showDescriptionText' // ✅ Show/Hide Toggle Support
        },
        {
            fieldName: 'youtubeUrl',
            label: 'Main Video URL',
            type: 'input',
            placeholder: 'https://www.youtube.com/watch?v=...'
        },
        {
            fieldName: 'secondVideoUrl',
            label: 'Second Video URL',
            type: 'input',
            placeholder: 'https://www.youtube.com/watch?v=...',
            showField: 'showSecondVideo'
        }
    ];

    // ✅ BUTTON WIDGET STYLE CONFIGURATION
    const buttonConfig = {
        textField: 'buttonText',
        linkField: 'buttonLink',
        showGradientControls: true,
        showShadowControls: true,
        showBorderControls: true,
        showAnimationControls: true
    };

    // Custom Layout Properties für RevolutionaryLayoutSettings
    const customLayoutProperties = [
        {
            section: 'Text Max Width Controls',
            icon: 'fa-text-width',
            properties: [
                {
                    id: 'richTextMaxWidth',
                    label: 'Rich Text Max Width',
                    min: 200,
                    max: 800,
                    step: 10,
                    unit: 'px',
                    icon: '📰',
                    description: 'Maximum width of the rich text container',
                    defaultValue: 600
                },
                {
                    id: 'descriptionMaxWidth',
                    label: 'Description Max Width',
                    min: 200,
                    max: 800,
                    step: 10,
                    unit: 'px',
                    icon: '📝',
                    description: 'Maximum width of the description text container',
                    defaultValue: 500
                }
            ]
        },
        {
            section: 'Video Settings',
            icon: 'fa-video',
            properties: [
                {
                    id: 'videoContainerWidth',
                    label: 'Video Width',
                    min: 20,
                    max: 99,
                    step: 5,
                    unit: '%',
                    icon: '📹',
                    description: 'Width of the video container (centered layouts: % of container)',
                    defaultValue: 45,
                    responsive: true // ✅ Macht Property responsive (videoContainerWidthMobile)
                },
                {
                    id: 'videoBorderRadius',
                    label: 'Main Video Border Radius',
                    min: 0,
                    max: 30,
                    step: 1,
                    unit: 'px',
                    icon: '🎞️',
                    description: 'Border radius of the main video player (0 = square corners)',
                    defaultValue: 12,
                    responsive: true // ✅ Macht Property responsive (videoBorderRadiusMobile)
                },
                {
                    id: 'secondVideoWidth',
                    label: 'Second Video Width',
                    min: 30,
                    max: 80,
                    step: 5,
                    unit: '%',
                    icon: '📹',
                    description: 'Width of the second video container (between title and description)',
                    defaultValue: 60,
                    responsive: true
                },
                {
                    id: 'secondVideoBorderRadius',
                    label: 'Second Video Border Radius',
                    min: 0,
                    max: 30,
                    step: 1,
                    unit: 'px',
                    icon: '🎞️',
                    description: 'Border radius of the second video player',
                    defaultValue: 8,
                    responsive: true
                }
            ]
        },
        {
            section: 'Decoration Image',
            icon: 'fa-image',
            properties: [
                {
                    id: 'decorationImageSize',
                    label: 'Decoration Size',
                    min: 50,
                    max: 1200,
                    step: 10,
                    unit: 'px',
                    icon: '🖼️',
                    description: 'Size of the decoration image',
                    defaultValue: 320,          // ✅ NEW DEFAULT: 320px
                    responsive: true // ✅ Macht Property responsive (decorationImageSizeMobile)
                },
                {
                    id: 'decorationImagePositionX',
                    label: 'Position X',
                    min: -30,
                    max: 130,
                    step: 5,
                    unit: '%',
                    icon: '↔️',
                    description: 'Horizontal position of decoration image (extended range)',
                    defaultValue: 80            // ✅ BLEIBT: 80%
                },
                {
                    id: 'decorationImagePositionY',
                    label: 'Position Y',
                    min: -70,
                    max: 130,
                    step: 5,
                    unit: '%',
                    icon: '↕️',
                    description: 'Vertical position of decoration image (extended range)',
                    defaultValue: 15            // ✅ NEW DEFAULT: 15%
                },
                {
                    id: 'decorationImageOpacity',
                    label: 'Decoration Opacity',
                    min: 0,
                    max: 100,
                    step: 5,
                    unit: '%',
                    icon: '👻',
                    description: 'Opacity of the decoration image',
                    defaultValue: 75            // ✅ NEW DEFAULT: 75%
                }
            ]
        },
        {
            section: 'Layout & Spacing',
            icon: 'fa-expand-arrows-alt',
            properties: [
                {
                    id: 'contentSpacing',
                    label: 'Content Spacing',
                    min: 0,
                    max: 120,
                    step: 5,
                    unit: 'px',
                    icon: '📏',
                    description: 'Space between text and video sections',
                    defaultValue: 20,
                    responsive: true // ✅ Macht Property responsive (contentSpacingMobile)
                },
                {
                    id: 'videoGap',
                    label: 'Video Gap',
                    min: 10,
                    max: 100,
                    step: 5,
                    unit: 'px',
                    icon: '🎬',
                    description: 'Gap between video and text in centered layouts',
                    defaultValue: 40,
                    responsive: true // ✅ Macht Property responsive (videoGapMobile)
                },
                {
                    id: 'headerImageHeight',
                    label: 'Header Height',
                    min: 60,
                    max: 300,
                    step: 10,
                    unit: 'px',
                    icon: '🖼️',
                    description: 'Height of the header image',
                    defaultValue: 120,
                    responsive: true // ✅ Macht Property responsive (headerImageHeightMobile)
                },
                {
                    id: 'iconHeight',
                    label: 'Icon Height',
                    min: 20,
                    max: 300,
                    step: 5,
                    unit: 'px',
                    icon: '🎯',
                    description: 'Height of the icon',
                    defaultValue: 60,
                    responsive: true // ✅ Macht Property responsive (iconHeightMobile)
                }
            ]
        }
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - Widget-spezifische Properties
    const advancedConfig = [
        // Text Alignment
        {
            fieldName: 'textAlignment',
            label: 'Text Alignment',
            type: 'dropdown',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'left',
            icon: '/img/down.svg'
        },
        {
            fieldName: 'textAlignmentMobile',
            label: 'Mobile Text Alignment',
            type: 'dropdown',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'center',
            icon: '/img/down.svg'
        },
        
        // Visibility Controls
        {
            fieldName: 'showRichText',
            label: 'Show Rich Text',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'showDescriptionText',
            label: 'Show Description',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showButtonText',
            label: 'Show Button',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/element.svg'
        },
        {
            fieldName: 'showHeaderImage',
            label: 'Show Header Image',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/image.svg'
        },
        {
            fieldName: 'showIcon',
            label: 'Show Icon',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/element.svg'
        },
        {
            fieldName: 'showVideo',
            label: 'Show Main Video',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/video.svg'
        },
        {
            fieldName: 'showSecondVideo',
            label: 'Show Second Video',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/video.svg'
        },
        {
            fieldName: 'showDecorationImage',
            label: 'Show Decoration Image',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/image.svg'
        },
        
        // ✅ NEU: Highlight Settings hinzufügen
        {
            fieldName: 'richTextHighlightColor',
            label: 'Rich Text Highlight Color',
            type: 'color',
            defaultValue: '#D4A574',
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'richTextHighlightWeight',
            label: 'Rich Text Highlight Weight',
            type: 'dropdown',
            options: [
                { value: '300', label: 'Light' },
                { value: '400', label: 'Normal' },
                { value: '500', label: 'Medium' },
                { value: '600', label: 'Semi Bold' },
                { value: '700', label: 'Bold' },
                { value: '800', label: 'Extra Bold' },
                { value: '900', label: 'Black' }
            ],
            defaultValue: '800',
            icon: '/img/text-fat.svg'
        },
        // ✅ NEU: Margin Controls für präzise Positionierung
        {
            fieldName: 'richTextHighlightMarginLeft',
            label: 'Highlight Margin Left',
            type: 'slider',
            defaultValue: 0,
            min: -50,        // ✅ Erweitert: -50px für Überlappungen
            max: 50,         // ✅ Erweitert: +50px für große Abstände
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'richTextHighlightMarginRight',
            label: 'Highlight Margin Right',
            type: 'slider',
            defaultValue: 0,
            min: -50,
            max: 50,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        }
    ];

    // Color Configuration
    const colorConfig = [
        [
            {
                fieldName: 'richTextColor',
                label: 'Rich Text',
                defaultValue: '#2c3e50',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'descriptionColor',
                label: 'Description',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#FFF8DC',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'buttonColor',
                label: 'Button',
                defaultValue: '#5C80BC',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'buttonTextColor',
                label: 'Button Text',
                defaultValue: '#FFFFFF',
                icon: '/img/text.svg'
            }
        ]
    ];

    // ✅ FONT CONFIGURATION - Für MoreSettingsSection (ResponsiveFontControls-kompatibel)
    const fontConfig = [
        {
            label: 'Rich Text',
            sizeField: 'richTextSize',
            weightField: 'richTextWeight',
            defaultSize: '28',
            defaultWeight: '700'
        },
        {
            label: 'Rich Text Highlights',
            sizeField: 'richTextHighlightSize',
            weightField: 'richTextHighlightWeight',
            defaultSize: '28',
            defaultWeight: '800'
        },
        {
            label: 'Description',
            sizeField: 'descriptionSize',
            weightField: 'descriptionWeight',
            defaultSize: '16',
            defaultWeight: '400'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* ✅ LAYOUT SELECTOR 2025 */}
                <LayoutSelector2025
                    widgetType="videoTeaser"
                    selectedLayout={editData.layoutType || 'classic-video-right'}
                    onLayoutChange={handleLayoutChange}
                />
                
                {/* ✅ IMAGE CONTAINERS */}
                <ImageContainer
                    label="Header Image"
                    fieldName="headerImage"
                    currentImage={editData.headerImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingHeader}
                    setIsUploading={setIsUploadingHeader}
                    showToggle={true}
                    toggleLabel="Show Header Image"
                    toggleFieldName="showHeaderImage"
                    toggleChecked={editData.showHeaderImage !== false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'slider',
                            label: 'Header Height',
                            fieldName: 'headerImageHeight',
                            defaultValue: 120,
                            min: 60,
                            max: 300,
                            step: 10,
                            unit: 'px'
                        },
                        {
                            type: 'slider',
                            label: 'Header Spacing',
                            fieldName: 'headerSpacing',
                            defaultValue: 40,
                            min: 0,
                            max: 80,
                            step: 5,
                            unit: 'px'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />
                
                <ImageContainer
                    label="Icon"
                    fieldName="iconImage"
                    currentImage={editData.iconImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingIcon}
                    setIsUploading={setIsUploadingIcon}
                    showToggle={true}
                    toggleLabel="Show Icon"
                    toggleFieldName="showIcon"
                    toggleChecked={editData.showIcon === true}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'slider',
                            label: 'Icon Height (Desktop)',
                            fieldName: 'iconHeight',
                            defaultValue: 60,
                            min: 20,
                            max: 300,
                            step: 5,
                            unit: 'px'
                        },
                        {
                            type: 'slider',
                            label: 'Icon Height (Mobile)',
                            fieldName: 'iconHeightMobile',
                            defaultValue: 50,
                            min: 20,
                            max: 300,
                            step: 5,
                            unit: 'px'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />
                
                <ImageContainer
                    label="Decoration Image"
                    fieldName="decorationImage"
                    currentImage={editData.decorationImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingDecoration}
                    setIsUploading={setIsUploadingDecoration}
                    showToggle={true}
                    toggleLabel="Show Decoration Image"
                    toggleFieldName="showDecorationImage"
                    toggleChecked={editData.showDecorationImage !== false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'slider',
                            label: 'Decoration Size',
                            fieldName: 'decorationImageSize',
                            defaultValue: 120,
                            min: 50,
                            max: 1200,
                            step: 10,
                            unit: 'px'
                        },
                        {
                            type: 'slider',
                            label: 'Position X',
                            fieldName: 'decorationImagePositionX',
                            defaultValue: 80,
                            min: -30,
                            max: 130,
                            step: 5,
                            unit: '%'
                        },
                        {
                            type: 'slider',
                            label: 'Position Y',
                            fieldName: 'decorationImagePositionY',
                            defaultValue: 20,
                            min: -70,
                            max: 130,
                            step: 5,
                            unit: '%'
                        },
                        {
                            type: 'slider',
                            label: 'Opacity',
                            fieldName: 'decorationImageOpacity',
                            defaultValue: 30,
                            min: 0,
                            max: 100,
                            step: 5,
                            unit: '%'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />
                
                {/* ✅ TEXT SETTINGS - Klassisches Toggle-System */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                />

                {/* ✅ BUTTON WIDGET STYLE */}
                <ButtonWidgetStyle 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    buttonConfig={buttonConfig}
                />

                {/* ✅ REVOLUTIONARY LAYOUT SETTINGS */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* ✅ ADVANCED SETTINGS */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* ✅ MORE SETTINGS (Colors + Fonts) */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig} // ✅ Font-Einstellungen wieder hinzugefügt
                    layoutConfig={[]} // Empty - moved to Revolutionary Layout Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>
        </div>
    );
}

export default VideoTeaserEdit;
